import React, { useState, useEffect } from "react";
import Decimal from "decimal.js";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Box
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon
} from "@mui/icons-material";

/*
  Props:
  - suppliers: { id, supplier }[]
  - projects: { id, projectKey, projectName }[]
  - contracts: { id, contract, totalTons, pricePerTon, projectId, supplierId,
      projectDrawDownOrder, fullyRetiredOn, Supplier, Project, allocations, tonsSold }[]
  - inventoryPools: { id, inventoryPool }[]
  - onAddContract({ supplierId, projectId, totalTons, pricePerTon,
      allocations: [{ inventoryPoolId, tonsAllocated }] })
  - onAddSupplier({ supplier })
  - onAddProject({ projectKey, projectName })
  - onUpdateContract({ id, data: { contract } })
  - onVoidContract(contractId)
*/
export function ContractsSection({
  suppliers,
  projects,
  contracts,
  inventoryPools,
  onAddContract,
  onAddSupplier,
  onAddProject,
  onUpdateContract,
  onVoidContract
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activePage, setActivePage] = useState(0);
  const [inactivePage, setInactivePage] = useState(0);
  const rowsPerPage = 25;
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);

  const [contractName, setContractName] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [totalTons, setTotalTons] = useState("");
  const [pricePerTon, setPricePerTon] = useState("");

  const [newSupplierName, setNewSupplierName] = useState("");
  const [newProjectName, setNewProjectName] = useState("");

  const [editAllocContract, setEditAllocContract] = useState(null);
  const [editAllocs, setEditAllocs] = useState([]);
  const [editAllocError, setEditAllocError] = useState("");
  const [voidConfirmContract, setVoidConfirmContract] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [pendingContract, setPendingContract] = useState(null);

  const handleEditAllocOpen = (contract) => {
    setEditAllocContract(contract);
    setEditAllocs(
      inventoryPools.map((pool) => {
        const existing = (contract.Allocations || contract.allocations || []).find(
          (a) => String(a.inventoryPoolId) === String(pool.id)
        );
        return {
          inventoryPoolId: pool.id,
          tonsAllocated: existing ? Number(existing.tonsAllocated).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : ""
        };
      })
    );
    setEditAllocError("");
  };

  const handleEditAllocSubmit = () => {
    if (!editAllocContract) { return; }
    const parsedAllocs = editAllocs.map((a) => ({
      inventoryPoolId: a.inventoryPoolId,
      tonsAllocated: parseFloat(String(a.tonsAllocated).replace(/,/g, "")) || 0
    }));
    for (const a of parsedAllocs) {
      const sold = new Decimal(editAllocContract.tonsSoldByPool?.[a.inventoryPoolId] || 0);
      if (new Decimal(a.tonsAllocated || 0).lessThan(sold)) {
        setEditAllocError(
          `Pool allocation cannot be less than tons already sold (${sold.toNumber()}) for that pool`
        );
        return;
      }
    }
    const total = parsedAllocs.reduce(
      (s, a) => s.plus(new Decimal(a.tonsAllocated || 0)),
      new Decimal(0)
    );
    const availableTons = new Decimal(editAllocContract.totalTons)
      .minus(new Decimal(editAllocContract.tonsSold || 0));
    if (!total.equals(availableTons)) {
      setEditAllocError(
        `Total allocated (${total.toNumber()}) must equal available tonnage (${availableTons.toNumber()})`
      );
      return;
    }
    onUpdateContract({
      id: editAllocContract.id,
      data: {
        allocations: parsedAllocs
      }
    });
    setEditAllocContract(null);
  };

  const [allocations, setAllocations] = useState([]);
  useEffect(() => {
    setAllocations(inventoryPools.map((pool) => ({ inventoryPoolId: pool.id, tonsAllocated: 0 })));
  }, [inventoryPools]);
  const [allocationError, setAllocationError] = useState("");

  const resetForm = () => {
    setContractName("");
    setSelectedSupplier("");
    setSelectedProject("");
    setTotalTons("");
    setPricePerTon("");

    setAllocations(
      inventoryPools.map((pool) => ({ inventoryPoolId: pool.id, tonsAllocated: 0 }))
    );
    setAllocationError("");
    setShowNewSupplier(false);
    setShowNewProject(false);
    setNewSupplierName("");
    setNewProjectName("");
  };

  const handleSubmit = () => {
    if (!contractName || !selectedSupplier || !selectedProject || !totalTons || !pricePerTon) {
      return;
    }
    const totalAllocated = allocations.reduce(
      (s, a) => s.plus(new Decimal(parseFloat(String(a.tonsAllocated).replace(/,/g, "")) || 0)),
      new Decimal(0)
    );
    const rawTons = parseFloat(totalTons.toString().replace(/,/g, "")) || 0;
    const rawPrice = parseFloat(pricePerTon.toString().replace(/,/g, "")) || 0;
    const contractTons = new Decimal(rawTons);
    if (!totalAllocated.equals(contractTons)) {
      setAllocationError(
        `Total allocated (${totalAllocated.toNumber()}) must equal contract tonnage (${rawTons})`
      );
      return;
    }
    const supplierName = suppliers.find((s) => s.id === selectedSupplier)?.supplier || "";
    const projectName = projects.find((p) => p.id === selectedProject)?.projectName || "";
    setPendingContract({
      contract: contractName,
      supplierId: selectedSupplier,
      projectId: selectedProject,
      supplierName,
      projectName,
      totalTons: rawTons,
      pricePerTon: rawPrice,
      allocations: allocations
        .map((a) => ({
          inventoryPoolId: a.inventoryPoolId,
          tonsAllocated: parseFloat(String(a.tonsAllocated).replace(/,/g, "")) || 0,
          poolName: inventoryPools.find((p) => p.id === a.inventoryPoolId)?.inventoryPool || String(a.inventoryPoolId)
        }))
        .filter((a) => a.tonsAllocated > 0)
    });
    setReviewOpen(true);
  };

  const handleConfirm = () => {
    const { supplierName, projectName, ...contractPayload } = pendingContract;
    const apiPayload = {
      ...contractPayload,
      allocations: contractPayload.allocations.map(({ poolName, ...a }) => a)
    };
    onAddContract(apiPayload);
    setReviewOpen(false);
    setIsOpen(false);
    resetForm();
    setPendingContract(null);
  };

  const handleReviewBack = () => {
    setReviewOpen(false);
    setPendingContract(null);
  };

  const handleAddSupplier = async () => {
    if (!newSupplierName) { return; }
    const created = await onAddSupplier({ supplier: newSupplierName });
    setNewSupplierName("");
    setShowNewSupplier(false);
    if (created?.id) { setSelectedSupplier(created.id); }
  };

  const toKey = (name) =>
    name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

  const handleAddProject = async () => {
    if (!newProjectName) { return; }
    const created = await onAddProject({ projectKey: toKey(newProjectName), projectName: newProjectName });
    setNewProjectName("");
    setShowNewProject(false);
    if (created?.id) { setSelectedProject(created.id); }
  };

  const activeContracts = contracts
    .filter((c) => c.fullyRetiredOn == null && c.voidedOn == null)
    .sort((a, b) => b.id - a.id);
  const inactiveContracts = contracts
    .filter((c) => c.fullyRetiredOn != null || c.voidedOn != null)
    .sort((a, b) => b.id - a.id);

  const renderContractTable = (rows, title, page, onPageChange, isActive) => {
    const pageRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    return (
      <Card sx={{ mb: 3 }}>
        <CardHeader title={<Typography variant="h6">{title}</Typography>} />
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Contract</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Tonnage</TableCell>
                  <TableCell>Tons Sold</TableCell>
                  <TableCell>Price/Ton</TableCell>
                  {inventoryPools.map((pool) => (
                    <TableCell key={pool.id}>{pool.inventoryPool}</TableCell>
                  ))}
                  <TableCell>{isActive ? "Actions" : "Reason"}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.length === 0
                  ? (
                    <TableRow>
                      <TableCell colSpan={7 + inventoryPools.length} align="center" sx={{ color: "text.secondary" }}>
                      No contracts
                      </TableCell>
                    </TableRow>
                  )
                  : (
                    pageRows.map((contract) => {
                      const supplier = contract.Supplier ||
                      suppliers.find((s) => s.id === contract.supplierId);
                      const project = contract.Project ||
                      projects.find((p) => p.id === contract.projectId);
                      return (
                        <TableRow
                          key={contract.id}
                          sx={!isActive ? { opacity: 0.6 } : {}}
                        >
                          <TableCell>{contract.id}</TableCell>
                          <TableCell>{contract.contract}</TableCell>
                          <TableCell>{supplier?.supplier || supplier?.name || ""}</TableCell>
                          <TableCell>{project?.projectName || project?.name || ""}</TableCell>
                          <TableCell>{Number(contract.totalTons || 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</TableCell>
                          <TableCell>{Number(contract.tonsSold || 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</TableCell>
                          <TableCell>${Number(contract.pricePerTon).toFixed(2)}</TableCell>
                          {inventoryPools.map((pool) => {
                            const allocation = (contract.Allocations || contract.allocations || []).find(
                              (a) => String(a.inventoryPoolId) === String(pool.id)
                            );
                            return (
                              <TableCell key={pool.id}>
                                {allocation ? Number(allocation.tonsAllocated).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : "-"}
                              </TableCell>
                            );
                          })}
                          <TableCell>
                            {isActive
                              ? (
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleEditAllocOpen(contract)}
                                  >
                                    Reallocate
                                  </Button>
                                  {!contract.voidedOn && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={() => setVoidConfirmContract(contract)}
                                    >
                                      Void
                                    </Button>
                                  )}
                                </Box>
                              )
                              : (
                                <Typography variant="body2" color="text.secondary">
                                  {contract.voidedOn
                                    ? `Voided on ${new Date(contract.voidedOn).toLocaleDateString()}`
                                    : contract.fullyRetiredOn
                                      ? `Fully sold ${new Date(contract.fullyRetiredOn).toLocaleDateString()}`
                                      : ""}
                                </Typography>
                              )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
              </TableBody>
            </Table>
          </Box>
          {rows.length > rowsPerPage && (
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(_, newPage) => onPageChange(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
            />
          )}
        </CardContent>
      </Card>
    );
  };

  const totalAllocated = allocations.reduce((sum, a) => sum + (parseFloat(String(a.tonsAllocated).replace(/,/g, "")) || 0), 0);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Contracts</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsOpen(true)}>
          Add Contract
        </Button>
      </Box>

      {renderContractTable(activeContracts, "Active Contracts", activePage, setActivePage, true)}
      {renderContractTable(inactiveContracts, "Inactive Contracts", inactivePage, setInactivePage, false)}

      <Dialog open={Boolean(editAllocContract)} onClose={() => setEditAllocContract(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Allocations — {editAllocContract?.contract}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, pb: 0.5, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Pool</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ width: 90, textAlign: "right" }}>Current</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ width: 90, textAlign: "right" }}>Sold</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ width: 140, textAlign: "right" }}>New Allocation</Typography>
            </Box>
            {(() => {
              const origAllocs = editAllocContract?.Allocations || editAllocContract?.allocations || [];
              const totalUnsold = new Decimal(editAllocContract?.totalTons || 0)
                .minus(new Decimal(editAllocContract?.tonsSold || 0));
              return inventoryPools.map((pool, idx) => {
                const soldForPool = Number(editAllocContract?.tonsSoldByPool?.[pool.id] || 0);
                const currentAlloc = origAllocs.find(
                  (a) => String(a.inventoryPoolId) === String(pool.id)
                );
                const currentTons = currentAlloc ? Number(currentAlloc.tonsAllocated) : 0;
                const unsoldElsewhere = editAllocs.reduce((s, a, i) => {
                  if (i === idx) { return s; }
                  const otherSold = Number(editAllocContract?.tonsSoldByPool?.[a.inventoryPoolId] || 0);
                  const parsedAlloc = parseFloat(String(a.tonsAllocated).replace(/,/g, "")) || 0;
                  return s.plus(new Decimal(Math.max(0, parsedAlloc - otherSold)));
                }, new Decimal(0));
                const maxForPool = new Decimal(soldForPool).plus(totalUnsold).minus(unsoldElsewhere).toNumber();
                return (
                  <Box key={pool.id} sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5 }}>
                    <Typography sx={{ flex: 1 }}>{pool.inventoryPool}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ width: 90, textAlign: "right" }}>
                      {currentTons.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ width: 90, textAlign: "right" }}>
                      {soldForPool.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                    </Typography>
                    <TextField
                      type="text"
                      size="small"
                      sx={{ width: 140 }}
                      placeholder="0.000"
                      value={editAllocs[idx]?.tonsAllocated || ""}
                      onChange={(e) => {
                        const updated = [...editAllocs];
                        updated[idx] = { inventoryPoolId: pool.id, tonsAllocated: e.target.value };
                        setEditAllocs(updated);
                        setEditAllocError("");
                      }}
                      onBlur={(e) => {
                        const raw = parseFloat(e.target.value.toString().replace(/,/g, "")) || 0;
                        const clamped = Math.min(Math.max(raw, soldForPool), maxForPool);
                        const updated = [...editAllocs];
                        updated[idx] = { inventoryPoolId: pool.id, tonsAllocated: clamped > 0 ? clamped.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : "" };
                        setEditAllocs(updated);
                      }}
                      onFocus={(e) => {
                        const raw = parseFloat(e.target.value.toString().replace(/,/g, ""));
                        const updated = [...editAllocs];
                        updated[idx] = { inventoryPoolId: pool.id, tonsAllocated: isNaN(raw) || raw === 0 ? "" : String(raw) };
                        setEditAllocs(updated);
                      }}
                    />
                  </Box>
                );
              });
            })()}
            <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1 }}>
              {(() => {
                const availableTotal = new Decimal(editAllocContract?.totalTons || 0)
                  .minus(new Decimal(editAllocContract?.tonsSold || 0));
                const allocated = editAllocs.reduce(
                  (s, a) => s.plus(new Decimal(parseFloat(String(a.tonsAllocated).replace(/,/g, "")) || 0)),
                  new Decimal(0)
                );
                const remaining = availableTotal.minus(allocated);
                return (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">
                      {`Total allocated: ${allocated.toNumber().toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} / ${availableTotal.toNumber().toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} tons`}
                    </Typography>
                    <Typography variant="body2" color={remaining.isZero() ? "success.main" : "text.secondary"}>
                      {`${remaining.toNumber().toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} remaining`}
                    </Typography>
                  </Box>
                );
              })()}
              {editAllocError && (
                <Typography variant="body2" color="error">{editAllocError}</Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAllocContract(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditAllocSubmit}>Save Allocations</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(voidConfirmContract)} onClose={() => setVoidConfirmContract(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Void Contract</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to void Contract <strong>{voidConfirmContract?.contract}</strong>? This contract will no longer be available for allocation or retirement. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidConfirmContract(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              onVoidContract?.(voidConfirmContract.id);
              setVoidConfirmContract(null);
            }}
          >
            Void Contract
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reviewOpen} onClose={handleReviewBack} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm New Contract</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please review the contract details before submitting.
          </Typography>
          {pendingContract && (
            <Box>
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ color: "text.secondary", border: 0, pl: 0 }}>Contract Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, border: 0 }}>{pendingContract.contract}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ color: "text.secondary", border: 0, pl: 0 }}>Supplier</TableCell>
                    <TableCell sx={{ border: 0 }}>{pendingContract.supplierName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ color: "text.secondary", border: 0, pl: 0 }}>Project</TableCell>
                    <TableCell sx={{ border: 0 }}>{pendingContract.projectName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ color: "text.secondary", border: 0, pl: 0 }}>Total Tonnage</TableCell>
                    <TableCell sx={{ border: 0 }}>
                      {Number(pendingContract.totalTons).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} tons
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ color: "text.secondary", border: 0, pl: 0 }}>Price per Ton</TableCell>
                    <TableCell sx={{ border: 0 }}>${Number(pendingContract.pricePerTon).toFixed(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
                Pool Allocations
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ pl: 0 }}>Pool</TableCell>
                    <TableCell align="right">Allocated Tons</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingContract.allocations.map((a) => (
                    <TableRow key={a.inventoryPoolId}>
                      <TableCell sx={{ pl: 0 }}>{a.poolName}</TableCell>
                      <TableCell align="right">
                        {Number(a.tonsAllocated).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReviewBack}>← Go Back</Button>
          <Button variant="contained" onClick={handleConfirm}>Confirm &amp; Create Contract</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isOpen} onClose={() => { setIsOpen(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>Add New Contract</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Contract Name"
              size="small"
              value={contractName}
              onChange={(e) => setContractName(e.target.value)}
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Supplier</Typography>
              {!showNewSupplier
                ? (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select supplier</InputLabel>
                      <Select
                        value={selectedSupplier}
                        label="Select supplier"
                        onChange={(e) => setSelectedSupplier(e.target.value)}
                      >
                        {suppliers.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.supplier}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={() => setShowNewSupplier(true)} sx={{ minWidth: 40 }}>
                      <AddIcon />
                    </Button>
                  </Box>
                )
                : (
                  <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      label="Supplier name"
                      size="small"
                      value={newSupplierName}
                      onChange={(e) => setNewSupplierName(e.target.value)}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button size="small" variant="contained" onClick={handleAddSupplier}>Add</Button>
                      <Button size="small" variant="outlined" onClick={() => setShowNewSupplier(false)}>Cancel</Button>
                    </Box>
                  </Box>
                )}
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Project</Typography>
              {!showNewProject
                ? (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select project</InputLabel>
                      <Select
                        value={selectedProject}
                        label="Select project"
                        onChange={(e) => setSelectedProject(e.target.value)}
                      >
                        {projects.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.projectName}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={() => setShowNewProject(true)} sx={{ minWidth: 40 }}>
                      <AddIcon />
                    </Button>
                  </Box>
                )
                : (
                  <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      label="Project name"
                      size="small"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button size="small" variant="contained" onClick={handleAddProject}>Add</Button>
                      <Button size="small" variant="outlined" onClick={() => setShowNewProject(false)}>Cancel</Button>
                    </Box>
                  </Box>
                )}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                label="Total Tonnage"
                type="text"
                size="small"
                value={totalTons}
                onChange={(e) => setTotalTons(e.target.value)}
                onBlur={() => {
                  const n = parseFloat(totalTons);
                  if (!isNaN(n)) { setTotalTons(n.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })); }
                }}
                onFocus={() => {
                  const n = parseFloat(totalTons.toString().replace(/,/g, ""));
                  if (!isNaN(n)) { setTotalTons(String(n)); }
                }}
              />
              <TextField
                label="Price per Ton"
                type="text"
                size="small"
                value={pricePerTon}
                onChange={(e) => setPricePerTon(e.target.value)}
                onBlur={() => {
                  const n = parseFloat(pricePerTon);
                  if (!isNaN(n)) { setPricePerTon(n.toFixed(2)); }
                }}
                onFocus={() => {
                  const n = parseFloat(pricePerTon.toString().replace(/,/g, ""));
                  if (!isNaN(n)) { setPricePerTon(String(n)); }
                }}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Inventory Pool Allocation</Typography>
              <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                {inventoryPools.map((pool, idx) => (
                  <Box key={pool.id} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ flex: 1 }}>{pool.inventoryPool}</Typography>
                    <TextField
                      type="text"
                      size="small"
                      placeholder="0.000"
                      sx={{ width: 120 }}
                      value={allocations[idx]?.tonsAllocated || ""}
                      onChange={(e) => {
                        const updated = [...allocations];
                        updated[idx] = {
                          inventoryPoolId: pool.id,
                          tonsAllocated: e.target.value
                        };
                        setAllocations(updated);
                        setAllocationError("");
                      }}
                      onBlur={(e) => {
                        const n = parseFloat(e.target.value.toString().replace(/,/g, ""));
                        const updated = [...allocations];
                        updated[idx] = {
                          inventoryPoolId: pool.id,
                          tonsAllocated: isNaN(n) ? 0 : n
                        };
                        if (!isNaN(n) && n > 0) {
                          updated[idx].tonsAllocated = n.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
                        }
                        setAllocations(updated);
                      }}
                      onFocus={(e) => {
                        const n = parseFloat(e.target.value.toString().replace(/,/g, ""));
                        const updated = [...allocations];
                        updated[idx] = {
                          inventoryPoolId: pool.id,
                          tonsAllocated: isNaN(n) || n === 0 ? "" : String(n)
                        };
                        setAllocations(updated);
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ width: 32 }}>tons</Typography>
                  </Box>
                ))}
                <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1 }}>
                  <Typography variant="body2">
                    Total allocated: {Number(totalAllocated).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} / {Number(parseFloat(String(totalTons || 0).replace(/,/g, "")) || 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} tons
                  </Typography>
                  {allocationError && (
                    <Typography variant="body2" color="error">{allocationError}</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsOpen(false); resetForm(); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>Create Contract</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
